import React, { useState, useCallback } from 'react';
import { Table, Pagination, Card } from 'react-bootstrap';
import { FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';
import dateformat from 'utils/dateformat';
import {
  getTableHeaderClass,
  getModernDataTableClass,
  getModernCardHeaderClass,
  getModernCardBodyClass,
  getModernCardFooterClass,
  getModernTableClass,
  getModernDataTableMetaClass,
} from 'utils/tableTheme';

const Results = ({ user, data, currentTheme, onSelect, selectedLogId, selectedLogsForReport, onToggleLogSelection, onToggleAllLogs }) => {

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(40);
  const [orderBy, setOrderBy] = useState('createdAt');
  const [orderDirection, setOrderDirection] = useState('desc');

  const handleChangePage = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0); // Reset to first page ao mudar linhas por página
  }, []);

  // Função para manipular o clique no cabeçalho e ordenar
  const handleSort = (column) => {
    if (orderBy === column) {
      // Inverte a direção se a coluna já está selecionada
      setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Define uma nova coluna e inicia com ascendente
      setOrderBy(column);
      setOrderDirection(column === 'time' ? 'desc' : 'asc');
    }
    // Resetar para primeira página quando mudar ordenação
    setPage(0);
  };

  // Função para ordenar os dados com base na coluna e direção
  const sortData = (dataToSort) => {
    if (!orderBy) return dataToSort;
    return [...dataToSort].sort((a, b) => {
      let valueA = a[orderBy];
      let valueB = b[orderBy];
      if (orderBy === 'createdAt') {
        valueA = new Date(valueA || 0).getTime();
        valueB = new Date(valueB || 0).getTime();
      }
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return orderDirection === 'asc' ? valueA - valueB : valueB - valueA;
      } else {
        const comparison = String(valueA || '').localeCompare(String(valueB || ''));
        return orderDirection === 'asc' ? comparison : -comparison;
      }
    });
  };

  // Renderizar ícone de ordenação usando react-icons
  const renderSortIcon = (column) => {
    if (orderBy !== column) {
      return <FaSort style={{ marginLeft: 5, opacity: 0.5 }} />;
    }
    return orderDirection === 'asc'
      ? <FaSortUp style={{ marginLeft: 5, opacity: 1 }} />
      : <FaSortDown style={{ marginLeft: 5, opacity: 1 }} />;
  };

  // Aplicar ordenação antes da paginação
  const sortedData = sortData(data);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const paginatedData = sortedData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const getPaginationRange = () => {
    const start = Math.max(page - 2, 0);
    const end = Math.min(page + 2, totalPages - 1);
    let range = [];

    for (let i = start; i <= end; i++) {
      range.push(i);
    }
    return range;
  };

  const renderCreatedAt = (item) => {
    const t = item?.createdAt;
    if (!t) return '--';
    const dt = new Date(t);
    if (isNaN(dt.getTime())) return String(t);
    return dateformat(dt, true);
  };

  return (
    <div>
      <p className={getModernDataTableMetaClass()}>
        {data.length} registros encontrados. Página {page + 1} de {totalPages}
      </p>
      <Card
        className={getModernDataTableClass(currentTheme)}
      >
        <Card.Header
          className={getModernCardHeaderClass(currentTheme)}
        >
          Audit Logs
        </Card.Header>
        <Card.Body
          className={getModernCardBodyClass(currentTheme)}
        >
          <Table
            style={{ fontSize: "0.9rem" }}
            hover
            responsive
            size="sm"
            className={getModernTableClass(currentTheme)}
          >
            <thead>
              <tr className={getTableHeaderClass(currentTheme)}>
                <th className="text-center" style={{ width: '50px' }}>
                  <input
                    type="checkbox"
                    checked={selectedLogsForReport && paginatedData.length > 0 && paginatedData.every(item => selectedLogsForReport.includes(item._id))}
                    onChange={() => onToggleAllLogs && onToggleAllLogs(paginatedData)}
                  />
                </th>
                <th
                  className="text-center col-sm-2"
                  onClick={() => handleSort('createdAt')}
                  style={{ cursor: 'pointer' }}
                >
                  Data/Hora {renderSortIcon('createdAt')}
                </th>
                <th
                  className="text-center col-sm-2"
                  onClick={() => handleSort('responsible')}
                  style={{ cursor: 'pointer' }}
                >
                  Responsável {renderSortIcon('responsible')}
                </th>
                <th
                  className="text-center col-sm-1"
                  onClick={() => handleSort('action')}
                  style={{ cursor: 'pointer' }}
                >
                  Ação {renderSortIcon('action')}
                </th>
                <th
                  className="text-center"
                  onClick={() => handleSort('route')}
                  style={{ cursor: 'pointer' }}
                >
                  Rota {renderSortIcon('route')}
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, idx) => {
                const isSelected = selectedLogId && item._id === selectedLogId;
                const isChecked = selectedLogsForReport && selectedLogsForReport.includes(item._id);
                const selectedStyle = isSelected ? {
                  backgroundColor: currentTheme === 'dark' ? '#0d6efd' : '#0d6efd',
                  color: '#fff',
                  fontWeight: 'bold'
                } : {};
                return (
                  <tr
                    key={item._id || idx}
                    style={{
                      cursor: onSelect ? 'pointer' : 'default',
                      ...selectedStyle
                    }}
                  >
                    <td className="text-center pt-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => onToggleLogSelection && onToggleLogSelection(item._id)}
                      />
                    </td>
                    <td className="text-center pt-2" onClick={() => onSelect && onSelect(item._id)}>{renderCreatedAt(item)}</td>
                    <td className="text-center pt-2" onClick={() => onSelect && onSelect(item._id)}>{item.responsible || '--'}</td>
                    <td className="text-center pt-2" onClick={() => onSelect && onSelect(item._id)}>{item.action || '--'}</td>
                    <td className="pt-2" onClick={() => onSelect && onSelect(item._id)}>{item.route || '--'}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
        <Card.Footer
          className={getModernCardFooterClass(currentTheme)}
        >
          <div>
            <label>
              Linhas por página:{' '}
              <select className="modern-data-table__rows-select"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}>
                {[20, 40, 60].map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <Pagination className="custom-pagination" variant="dark">
            <Pagination.Prev
              onClick={() => handleChangePage(page - 1)}
              disabled={page === 0}
              aria-label="Página anterior"
            />
            {getPaginationRange().map((index) => (
              <Pagination.Item
                key={index}
                active={index === page}
                onClick={() => handleChangePage(index)}
              >
                {index + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => handleChangePage(page + 1)}
              disabled={page >= totalPages - 1}
              aria-label="Próxima página"
            />
          </Pagination>
        </Card.Footer>
      </Card>
    </div>
  );
};

export default Results;
